import { effects, listen, reactive, system } from "kho";
import { Group, Object3D, Vector3 } from "three";
import { $ifcFactory, $objectScale } from "../data/ifc";
import {
  $deleteRequest,
  $editorMode,
  $instances,
  $instancesGroup,
  $placeRequest,
  $selection,
  $transformRequest,
  Instance,
} from "../data/model";
import { $scene } from "../data/scene";

/**
 * instancesSystem — ported from poc/editor system/instances.ts. Sole writer of
 * $instances; reconciles the data map into THREE objects. The only client
 * change: meshes are built from IFC via the async $ifcFactory (not synchronous
 * GLB templates), so creation lands a frame or two later.
 */
export const instancesSystem = system((scope) => {
  const { atoms, maps } = scope(reactive);
  const { effect, onDispose } = scope(effects);
  const { on } = scope(listen);

  const group = new Group();
  const objects = new Map<string, Object3D>();
  const loading = new Set<string>();

  effect([$scene], () => {
    const scene = atoms.get($scene);
    if (scene && group.parent !== scene) {
      scene.add(group);
      atoms.set($instancesGroup, group);
    }
  });

  on($placeRequest, (req) => {
    if (!req) return;
    const id = crypto.randomUUID();
    maps.set($instances, id, {
      id,
      url: req.url,
      committedId: req.committedId ?? null,
      position: req.position,
      quaternion: req.quaternion,
      up: req.up,
      baseYaw: 0,
      height: 0,
      scale: atoms.get($objectScale) ?? 1,
    });
    atoms.set($selection, id);
    atoms.set($editorMode, "selected"); // fresh placement gets focus
  });

  // partial transform: merge only the fields the gizmo actually changed
  on($transformRequest, (req) => {
    if (!req) return;
    const inst = maps.get($instances, req.id);
    if (!inst) return;
    maps.set($instances, req.id, {
      ...inst,
      ...(req.position ? { position: req.position } : {}),
      ...(req.quaternion ? { quaternion: req.quaternion } : {}),
      ...(req.up ? { up: req.up } : {}),
      ...(req.baseYaw !== undefined ? { baseYaw: req.baseYaw } : {}),
      ...(req.height !== undefined ? { height: req.height } : {}),
      ...(req.scale !== undefined ? { scale: req.scale } : {}),
    });
  });

  on($deleteRequest, (req) => {
    if (!req) return;
    maps.delete($instances, req.id);
    if (atoms.get($selection) === req.id) {
      atoms.set($selection, null);
      atoms.set($editorMode, "idle");
    }
  });

  // render = PARENT pivot + CHILD lift along the pivot's up
  const applyTransform = (obj: Object3D, inst: Instance) => {
    obj.scale.setScalar(inst.scale);
    obj.position
      .fromArray(inst.position)
      .addScaledVector(new Vector3().fromArray(inst.up), inst.height);
    obj.quaternion.fromArray(inst.quaternion);
    obj.updateMatrixWorld(true);
  };

  // reconcile data → scene objects
  effect([$instances], () => {
    const instances = atoms.get($instances);
    for (const [id, obj] of objects) {
      if (!instances.has(id)) {
        group.remove(obj);
        objects.delete(id);
      }
    }
    for (const inst of instances.values()) {
      const obj = objects.get(inst.id);
      if (obj) {
        applyTransform(obj, inst);
        continue;
      }
      if (loading.has(inst.id)) continue;
      const factory = atoms.get($ifcFactory);
      if (!factory) continue;
      loading.add(inst.id);
      factory(inst.url).then((created) => {
        loading.delete(inst.id);
        const cur = maps.get($instances, inst.id);
        if (!created || !cur) return; // deleted / stale while loading
        created.userData.instanceId = inst.id;
        created.traverse((c) => {
          c.userData.instanceId = inst.id;
        });
        group.add(created);
        objects.set(inst.id, created);
        applyTransform(created, cur);
      });
    }
  });

  onDispose(() => {
    group.parent?.remove(group);
    atoms.set($instancesGroup, null);
  });
});
